import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll } from "framer-motion";
import { Toaster } from "sonner";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RivetDock from "@/components/denim/RivetDock";
import DenimIntro from "@/components/denim/DenimIntro";
import RouteZipper from "@/components/denim/RouteZipper";
import WishlistDrawer from "@/components/denim/WishlistDrawer";
import FloatingAccents from "@/components/denim/FloatingAccents";
import { CartProvider } from "@/components/denim/CartContext";
import PocketCart from "@/components/denim/PocketCart";
import RivetCursor from "@/components/denim/RivetCursor";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Shop from "@/pages/Shop";
import Contact from "@/pages/Contact";
import Account from "@/pages/Account";
import Product from "@/pages/Product";

function SmoothScroll() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function ScrollProgress() {
    const { scrollYProgress } = useScroll();
    return (
        <motion.div
            data-testid="scroll-progress"
            style={{ scaleX: scrollYProgress }}
            className="fixed inset-x-0 top-0 z-[70] h-[2px] origin-left bg-gold"
        />
    );
}

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { err: false };
    }
    static getDerivedStateFromError() {
        return { err: true };
    }
    render() {
        if (this.state.err) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-obsidian font-body text-ink">
                    Something went wrong — please refresh the page.
                </div>
            );
        }
        return this.props.children;
    }
}

export default function App() {
    return (
        <ErrorBoundary>
            <div className="grain" aria-hidden="true" />
            <DenimIntro />
            <BrowserRouter>
                <SmoothScroll />
                <RouteZipper />
                <ScrollProgress />
                <Navbar />
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/product/:id" element={<Product />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Footer />
                <FloatingAccents />
                <RivetDock />
                <PocketCart />
                <WishlistDrawer />
                <RivetCursor />
                <Toaster
                    theme="dark"
                    position="bottom-center"
                    toastOptions={{
                        style: {
                            background: "#18181B",
                            border: "1px solid rgba(255,255,255,0.1)",
                            color: "#F4F4F5",
                            borderRadius: "0",
                        },
                    }}
                />
            </BrowserRouter>
        </ErrorBoundary>
    );
}
